import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { useForm, useFieldArray } from "react-hook-form";
import "./admin.css";

interface Leader {
  name: string;
  role: string;
}

interface Organization {
  id: string;
  name: string;
  sector: string;
  is_sanitas: boolean;
  has_ccm: boolean;
  contact: {
    leaders: Leader[];
    phone: string;
    email: string;
    address: string;
  };
}

export function AdminOrganizatiiMembrePage() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  const { register, control, handleSubmit, reset } = useForm<Organization>({
    defaultValues: {
      id: "",
      name: "",
      sector: "",
      is_sanitas: false,
      has_ccm: false,
      contact: {
        leaders: [{ name: "", role: "" }],
        phone: "",
        email: "",
        address: "",
      },
    },
  });
  const { fields, append, remove } = useFieldArray({
    name: "contact.leaders",
    control,
  });

  // Încarcă lista de organizații
  const fetchOrganizations = async () => {
    const ref = doc(db, "site_content", "member_organizations");
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const raw = snap.data().organizations || [];
      const norm: Organization[] = raw.map((o: any) => ({
        ...o,
        contact: {
          leaders:
            o.contact?.leaders ||
            (o.contact?.leader_name
              ? [{ name: o.contact.leader_name, role: "" }]
              : []),
          phone: o.contact?.phone || "",
          email: o.contact?.email || "",
          address: o.contact?.address || "",
        },
      }));
      setOrganizations(norm);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Pregătește formularul pentru creație
  const handleNew = () => {
    setSelectedOrgId(null);
    reset({
      id: "",
      name: "",
      sector: "",
      is_sanitas: false,
      has_ccm: false,
      contact: { leaders: [{ name: "", role: "" }], phone: "", email: "", address: "" },
    });
  };

  // Populează formularul pentru edit
  const handleEdit = (org: Organization) => {
    setSelectedOrgId(org.id);
    reset(org);
  };

  // Creează sau actualizează organizația în Firestore
  const onSubmit = async (data: Organization) => {
    const ref = doc(db, "site_content", "member_organizations");
    const snap = await getDoc(ref);
    const allOrgs = snap.exists() ? snap.data().organizations : [];
    if (selectedOrgId) {
      const updated = allOrgs.map((o: any) =>
        o.id === selectedOrgId ? { ...data, id: selectedOrgId } : o
      );
      await updateDoc(ref, { organizations: updated });
    } else {
      const newId = Date.now().toString();
      const newOrg = { ...data, id: newId };
      await updateDoc(ref, { organizations: [...allOrgs, newOrg] });
    }
    await fetchOrganizations();
    handleNew();
  };

  return (
    <div className="admin-page">
      <h1>Zona Admin – Organizații Membre</h1>
      <div className="admin-content">
        {/* Formular creare / edit */}
        <div className="form-section">
          <h2>{selectedOrgId ? "Editează Organizație" : "Adaugă Organizație Nouă"}</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="org-form">
            <input {...register("name")} placeholder="Denumire" required />
            <input {...register("sector")} placeholder="Sector" />

            <label>
              <input type="checkbox" {...register("is_sanitas")} />
              Organizație Sanitas
            </label>
            <label>
              <input type="checkbox" {...register("has_ccm")} />
              Contract CCM
            </label>

            <section>
              <h4>Lider(i)</h4>
              {fields.map((field, idx) => (
                <div key={field.id} className="leader-row">
                  <input
                    {...register(`contact.leaders.${idx}.name` as const)}
                    placeholder="Nume lider"
                    required
                  />
                  <input
                    {...register(`contact.leaders.${idx}.role` as const)}
                    placeholder="Funcție lider"
                  />
                  <button type="button" onClick={() => remove(idx)}>
                    Șterge
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => append({ name: "", role: "" })}>
                + Adaugă lider
              </button>
            </section>

            <input {...register("contact.phone")} placeholder="Telefon" />
            <input {...register("contact.email")} placeholder="Email" />
            <input {...register("contact.address")} placeholder="Adresă" />

            <button type="submit">
              {selectedOrgId ? "Salvează modificările" : "Creează organizație"}
            </button>
          </form>
        </div>

        {/* Listă organizații */}
        <div className="list-section">
          <div className="list-header">
            <h2>Lista Organizații</h2>
            <button onClick={handleNew} className="new-btn">
              + Nou
            </button>
          </div>
          <ul className="org-list">
            {organizations.map((org) => (
              <li key={org.id} className="org-item">
                <div>
                  <strong>{org.name}</strong> ({org.sector})
                </div>
                <button onClick={() => handleEdit(org)}>Editează</button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
